import { useState } from 'react';
import type { ReactElement } from 'react';
import { Menu, X, LogOut, FileQuestionMark } from 'lucide-react';
import logoUrl from '/src/logo-white.svg';

interface HeaderProps {
  onBack?: () => void;
  currentSection?: 'reprove' | 'reports' | 'admin';
  onNavigate?: (section: 'reprove' | 'reports' | 'admin') => void;
  onLogout?: () => void;
  onNavigateToSurvey?: () => void;
  hasReproveAccess?: boolean;
}

const Header = ({
  onBack,
  currentSection = 'reprove',
  onNavigate,
  onLogout,
  onNavigateToSurvey,
  hasReproveAccess = false,
}: HeaderProps): ReactElement => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Obtener usuario del localStorage
  const user = ((): { name?: string; role?: string } | null => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        name?: string;
        role?: string;
      } | null;
    } catch {
      return null;
    }
  })();

  const canSeeReports =
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'GENERAL_MANAGER' ||
    user?.role === 'ZONE_MANAGER' ||
    user?.role === 'MKT';

  const canSeeAdmin = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ADMIN';

  const handleReportsClick = (): void => {
    if (onNavigate) {
      onNavigate('reports');
      setIsMobileNavOpen(false);
    } else {
      console.warn('⚠️ onNavigate no está definido');
    }
  };

  const handleReproveClick = (): void => {
    if (onNavigate) {
      onNavigate('reprove');
      setIsMobileNavOpen(false);
    } else {
      console.warn('⚠️ onNavigate no está definido');
    }
  };

  const handleAdminClick = (): void => {
    if (onNavigate) {
      onNavigate('admin');
      setIsMobileNavOpen(false);
    } else {
      console.warn('⚠️ onNavigate no está definido');
    }
  };

  const handleLogoClick = (): void => {
    if (onBack) {
      onBack();
      setIsMobileNavOpen(false);
    } else {
      console.warn('⚠️ onBack no está definido');
    }
  };

  const handleLogoutClick = (): void => {
    if (onLogout) {
      onLogout();
      setIsMobileNavOpen(false);
    }
  };

  const handleNavigateToSurvey = (): void => {
    if (onNavigateToSurvey) {
      onNavigateToSurvey();
      setIsMobileNavOpen(false);
    }
  };

  return (
    <header className="bg-primary-dark text-white h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 shadow-sm relative z-50">
      {/* Logo */}
      <div
        className="cursor-pointer hover:opacity-80 transition-opacity"
        onClick={handleLogoClick}
        title="Volver al dashboard"
      >
        <img
          src={logoUrl}
          alt="Bomuca Logo"
          className="w-20 sm:w-24 md:w-28"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
            const fallback = document.createElement('div');
            fallback.textContent = 'BOMUCA';
            fallback.className = 'text-white font-bold text-base sm:text-lg';
            e.currentTarget.parentNode?.appendChild(fallback);
          }}
        />
      </div>

      {/* Mobile Button */}
      <button
        onClick={() => {
          setIsMobileNavOpen(!isMobileNavOpen);
        }}
        className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
        aria-label={isMobileNavOpen ? 'Cerrar menú' : 'Abrir menú'}
      >
        {isMobileNavOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Menu className="w-6 h-6" />
        )}
      </button>

      {/* Desktop Navigation - Oculto en móvil */}
      <nav className="hidden lg:flex items-center gap-2">
        {canSeeAdmin && (
          <button
            onClick={handleAdminClick}
            className={`px-3 py-1 rounded transition-colors text-sm font-medium ${
              currentSection === 'admin'
                ? 'bg-white/20 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/90 hover:text-white'
            }`}
            title="Panel de administración"
          >
            Administración
          </button>
        )}

        {canSeeReports && (
          <button
            onClick={handleReportsClick}
            className={`px-3 py-1 rounded transition-colors text-sm font-medium ${
              currentSection === 'reports'
                ? 'bg-white/20 text-white'
                : 'bg-white/10 hover:bg-white/20 text-white/90 hover:text-white'
            }`}
            title="Ver reportes de evaluación"
          >
            Reportes
          </button>
        )}

        <button
          onClick={handleReproveClick}
          className={`px-3 py-1 rounded transition-colors text-sm font-medium ${
            currentSection === 'reprove'
              ? 'bg-white/20 text-white'
              : 'bg-white/10 hover:bg-white/20 text-white/90 hover:text-white'
          }`}
          title="Proceso de venta OTC"
        >
          Evaluación PDV
        </button>

        {/* Información del usuario */}
        <div className="ml-4 text-sm text-white/90 flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <span className="hidden xl:inline">{user?.name ?? 'Usuario'}</span>

          {/* Badge de rol */}
          {user?.role === 'SYSTEM_ADMIN' && (
            <span className="bg-red-600 text-white text-xs px-2 py-1 rounded font-medium">
              SYS ADMIN
            </span>
          )}
          {user?.role === 'ADMIN' && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
              ADMIN
            </span>
          )}
          {user?.role === 'GENERAL_MANAGER' && (
            <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded font-medium hidden xl:inline">
              GERENTE
            </span>
          )}
          {user?.role === 'MKT' && (
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded font-medium hidden xl:inline">
              MARKETING
            </span>
          )}
        </div>
      </nav>

      {/* Mobile Navigation Dropdown */}
      {isMobileNavOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-primary-dark border-t border-white/10 shadow-lg z-50">
          <nav className="flex flex-col p-4 space-y-2">
            {canSeeAdmin && (
              <button
                onClick={handleAdminClick}
                className={`w-full text-left px-4 py-3 rounded transition-colors text-sm font-medium ${
                  currentSection === 'admin'
                    ? 'bg-white/20 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white/90'
                }`}
              >
                Administración
              </button>
            )}

            {canSeeReports && (
              <button
                onClick={handleReportsClick}
                className={`w-full text-left px-4 py-3 rounded transition-colors text-sm font-medium ${
                  currentSection === 'reports'
                    ? 'bg-white/20 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white/90'
                }`}
              >
                Reportes
              </button>
            )}

            <button
              onClick={handleReproveClick}
              className={`w-full text-left px-4 py-3 rounded transition-colors text-sm font-medium ${
                currentSection === 'reprove'
                  ? 'bg-white/20 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white/90'
              }`}
            >
              Dashboard
            </button>

            {hasReproveAccess && (
              <button
                onClick={handleNavigateToSurvey}
                className="w-full text-left px-4 py-3 rounded bg-white/10 hover:bg-white/20 text-white/90 font-medium transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileQuestionMark className="w-5 h-5" />
                  <span>Realizar Encuesta</span>
                </div>
              </button>
            )}

            <div className="border-t border-white/10 my-2"></div>

            <button
              onClick={handleLogoutClick}
              className="w-full text-left px-4 py-3 rounded bg-red-50/10 hover:bg-red-500/20 text-red-300 hover:text-white font-medium transition-colors"
            >
              <div className="flex items-center gap-2">
                <LogOut className="w-5 h-5" />
                <span>Cerrar Sesión</span>
              </div>
            </button>

            {/* User info en móvil */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center gap-3 px-4 py-2">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1">
                  <p className="text-left text-sm font-medium">
                    {user?.name ?? 'Usuario'}
                  </p>
                  {user?.role && (
                    <p className="text-left text-xs text-white/70">
                      {user.role === 'SYSTEM_ADMIN' &&
                        'Administrador del Sistema'}
                      {user.role === 'ADMIN' && 'Administrador'}
                      {user.role === 'MKT' && 'Marketing'}
                      {user.role === 'GENERAL_MANAGER' && 'Gerente General'}
                      {user.role === 'ZONE_MANAGER' && 'Gerente de Zona'}
                      {user.role === 'REPRESENTATIVE' && 'Representante'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
